import { useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';

import { View } from '@actual-app/components/view';

import { color } from '#components/mobile/ui/tokens';

import { monedasDe } from './mover';

/** Lo que tarda en caer una moneda, de despegar a quedarse quieta. */
const MS_CAIDA = 700;
/** Hueco entre una moneda y la siguiente. */
const MS_ENTRE = 140;
/** Cuánto dura la caída entera (con la última moneda), para cerrar luego. */
export const MS_MONEDAS = MS_CAIDA + 2 * MS_ENTRE;

/** Alto de la cara de la moneda, grosor del canto y paso del montón. */
const ANCHO = 34;
const CARA = 13;
const CANTO = 4;
const PASO = 5;
/** Base (borde de abajo del montón) desde arriba de la carta y desde abajo. */
const BASE_ARRIBA = 66;
const BASE_ABAJO = 16;
const IZQUIERDA = 14;

const ORO = '#e2b65a';
const ORO_CLARO = '#f6dc98';
const ORO_OSCURO = '#a87a2a';
const TINTA = '#7a5718';

type MontonesProps = {
  /** Disponible del origen y del destino (`null`: aún sin elegir). */
  saldoArriba: number | null | undefined;
  saldoAbajo: number | null | undefined;
  /** Monedas que se irán (resaltadas un poco) y que caen al mover. */
  caen: number;
  /** Al pasar a `true`, caen las monedas del montón de arriba al de abajo. */
  soltando: boolean;
};

/**
 * Los dos montones de «Mover dinero» (Vadik, 24-oct-2026): arriba el de
 * «Desde», abajo el de «Hacia», de más alto cuanto más Disponible. A 0, un
 * hueco vacío; en rojo, un hueco con borde rojo. Al mover, las monedas de
 * arriba despegan, giran al caer y aterrizan en el montón de abajo con un
 * rebote pequeño (si había hueco rojo, se tapa). Va en la columna
 * izquierda de la carta (posición absoluta); la carta debe ser `relative`.
 */
export function Montones({
  saldoArriba,
  saldoAbajo,
  caen,
  soltando,
}: MontonesProps) {
  const raiz = useRef<HTMLDivElement>(null);
  const nArriba = monedasDe(saldoArriba);
  const nAbajo = monedasDe(saldoAbajo);

  useEffect(() => {
    const el = raiz.current;
    const carta = el?.parentElement;
    if (!soltando || !el || !carta || typeof el.animate !== 'function') {
      return;
    }
    const alto = carta.offsetHeight;
    const baseAbajo = alto - BASE_ABAJO;
    const animaciones: Animation[] = [];
    const cayendo = Array.from(
      el.querySelectorAll<HTMLElement>('[data-moneda-cae]'),
    );
    cayendo.forEach((moneda, j) => {
      // Sale de lo alto del montón de arriba (o del hueco si no hay
      // monedas) y aterriza encima del de abajo.
      const desdeArriba = BASE_ARRIBA - Math.max(nArriba - 1 - j, 0) * PASO;
      const hastaAbajo = baseAbajo - (nAbajo + j) * PASO;
      const dy = hastaAbajo - desdeArriba;
      const retraso = j * MS_ENTRE;
      animaciones.push(
        moneda.animate(
          [
            { transform: 'translateY(0) scaleY(1)', opacity: 1, offset: 0 },
            {
              transform: 'translateY(-9px) scaleY(.35)',
              opacity: 1,
              offset: 0.16,
              easing: 'cubic-bezier(.55,0,.9,.45)',
            },
            {
              transform: `translateY(${dy * 0.45}px) scaleY(1)`,
              offset: 0.48,
              easing: 'cubic-bezier(.4,0,1,1)',
            },
            {
              transform: `translateY(${dy}px) scale(1.12, .78)`,
              offset: 0.8,
              easing: 'ease-out',
            },
            {
              transform: `translateY(${dy - 4}px) scale(.97, 1.04)`,
              offset: 0.9,
              easing: 'ease-in',
            },
            {
              transform: `translateY(${dy}px) scale(1)`,
              opacity: 1,
              offset: 1,
            },
          ],
          { duration: MS_CAIDA, delay: retraso, fill: 'both' },
        ),
      );
      // La que se va del montón de arriba desaparece al despegar.
      const original = el.querySelector<HTMLElement>(
        `[data-moneda-arriba="${nArriba - 1 - j}"]`,
      );
      if (original) {
        animaciones.push(
          original.animate([{ opacity: 1 }, { opacity: 0 }], {
            duration: 1,
            delay: retraso,
            fill: 'forwards',
          }),
        );
      }
      // Sombra y destello donde aterriza.
      const sombra = el.querySelector<HTMLElement>('[data-sombra-abajo]');
      if (sombra && j === 0) {
        animaciones.push(
          sombra.animate(
            [
              { opacity: 0, transform: 'scale(.4)' },
              { opacity: 0.35, transform: 'scale(1)', offset: 0.8 },
              { opacity: 0, transform: 'scale(1.1)' },
            ],
            { duration: MS_CAIDA, delay: retraso, fill: 'both' },
          ),
        );
      }
    });
    const hueco = el.querySelector<HTMLElement>('[data-hueco-abajo]');
    if (hueco) {
      animaciones.push(
        hueco.animate([{ opacity: 1 }, { opacity: 0 }], {
          duration: 160,
          delay: MS_CAIDA * 0.8,
          fill: 'forwards',
        }),
      );
    }
    const destello = el.querySelector<HTMLElement>('[data-destello-abajo]');
    if (destello) {
      animaciones.push(
        destello.animate(
          [
            { opacity: 0, transform: 'scale(.6)' },
            { opacity: 0.9, transform: 'scale(1)', offset: 0.3 },
            { opacity: 0, transform: 'scale(1.5)' },
          ],
          {
            duration: 420,
            delay: MS_CAIDA * 0.8 + (cayendo.length - 1) * MS_ENTRE,
            fill: 'both',
          },
        ),
      );
    }
    return () => animaciones.forEach(a => a.cancel());
  }, [soltando, nArriba, nAbajo]);

  return (
    <View
      innerRef={raiz}
      aria-hidden
      data-testid="mover-montones"
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: 62,
        pointerEvents: 'none',
      }}
    >
      {/* Estela de puntos entre los dos montones. */}
      <View
        style={{
          position: 'absolute',
          left: IZQUIERDA + ANCHO / 2 - 1,
          top: BASE_ARRIBA + 8,
          bottom: BASE_ABAJO + CARA + CANTO + 8,
          width: 2,
          background: `repeating-linear-gradient(180deg, ${color.fg3} 0 4px, transparent 4px 9px)`,
          opacity: 0.35,
        }}
      />
      <Monton
        lado="arriba"
        saldo={saldoArriba}
        n={nArriba}
        resaltadas={soltando ? 0 : caen}
      />
      <Monton lado="abajo" saldo={saldoAbajo} n={nAbajo} resaltadas={0} />
      {/* Las que caen: encima del montón de arriba, quietas hasta soltar. */}
      {soltando &&
        Array.from({ length: caen }, (_, j) => (
          <Moneda
            key={j}
            data-moneda-cae={j}
            y={BASE_ARRIBA - Math.max(nArriba - 1 - j, 0) * PASO}
            arriba
            cara
            style={{ zIndex: 3 + j }}
          />
        ))}
    </View>
  );
}

function Monton({
  lado,
  saldo,
  n,
  resaltadas,
}: {
  lado: 'arriba' | 'abajo';
  saldo: number | null | undefined;
  n: number;
  resaltadas: number;
}) {
  const arriba = lado === 'arriba';
  const base = arriba ? BASE_ARRIBA : BASE_ABAJO;
  const enRojo = saldo != null && saldo < 0;
  return (
    <>
      {/* Hueco: siempre debajo; se ve cuando no hay monedas. */}
      {n === 0 && (
        <View
          {...(arriba ? {} : { 'data-hueco-abajo': true })}
          style={{
            position: 'absolute',
            left: IZQUIERDA,
            [arriba ? 'top' : 'bottom']: arriba ? base - CARA : base,
            width: ANCHO,
            height: CARA,
            borderRadius: '50%',
            border: `1.5px dashed ${enRojo ? color.bad : color.fg3}`,
            backgroundColor: enRojo
              ? `color-mix(in oklab, ${color.bad} 14%, transparent)`
              : 'transparent',
            opacity: saldo == null ? 0.45 : 0.8,
          }}
        />
      )}
      {!arriba && (
        <>
          <View
            data-sombra-abajo
            style={{
              position: 'absolute',
              left: IZQUIERDA - 3,
              bottom: BASE_ABAJO - 4,
              width: ANCHO + 6,
              height: 8,
              borderRadius: '50%',
              backgroundColor: '#0e1424',
              opacity: 0,
              filter: 'blur(2px)',
            }}
          />
          <View
            data-destello-abajo
            style={{
              position: 'absolute',
              left: IZQUIERDA - 6,
              bottom: BASE_ABAJO + n * PASO - 4,
              width: ANCHO + 12,
              height: CARA + 10,
              borderRadius: '50%',
              boxShadow: `0 0 0 2px ${ORO_CLARO}, 0 0 14px 4px ${ORO_CLARO}`,
              opacity: 0,
            }}
          />
        </>
      )}
      {Array.from({ length: n }, (_, i) => (
        <Moneda
          key={i}
          {...(arriba ? { 'data-moneda-arriba': i } : {})}
          y={arriba ? base - i * PASO : base + i * PASO}
          arriba={arriba}
          cara={i === n - 1}
          levantada={arriba && i >= n - resaltadas}
          style={{ zIndex: 1 + i }}
        />
      ))}
    </>
  );
}

/**
 * Una moneda vista de lado (cara y canto), de latón con la estrella de la
 * rosa de los vientos grabada en la de arriba. `y` es su borde de abajo:
 * desde arriba de la carta (`arriba`) o desde abajo.
 */
function Moneda({
  y,
  arriba,
  cara,
  levantada = false,
  style,
  ...datos
}: {
  y: number;
  arriba: boolean;
  cara: boolean;
  levantada?: boolean;
  style?: CSSProperties;
  [dato: `data-${string}`]: unknown;
}) {
  const alto = CARA + CANTO;
  return (
    <View
      {...datos}
      style={{
        position: 'absolute',
        left: IZQUIERDA,
        ...(arriba ? { top: y - alto } : { bottom: y }),
        width: ANCHO,
        height: alto,
        transform: levantada ? 'translateY(-2px)' : undefined,
        transition: 'transform 200ms ease-out',
        filter: levantada
          ? `drop-shadow(0 0 3px ${ORO_CLARO})`
          : 'drop-shadow(0 1px 1px rgba(14,20,36,.25))',
        ...style,
      }}
    >
      <svg viewBox={`0 0 ${ANCHO} ${alto}`} width={ANCHO} height={alto}>
        {/* Canto */}
        <path
          d={`M0 ${CARA / 2} v${CANTO} a${ANCHO / 2} ${CARA / 2} 0 0 0 ${ANCHO} 0 v-${CANTO} Z`}
          fill={ORO_OSCURO}
        />
        {/* Cara */}
        <ellipse
          cx={ANCHO / 2}
          cy={CARA / 2}
          rx={ANCHO / 2}
          ry={CARA / 2}
          fill={ORO}
        />
        <ellipse
          cx={ANCHO / 2}
          cy={CARA / 2}
          rx={ANCHO / 2 - 3}
          ry={CARA / 2 - 1.6}
          fill="none"
          stroke={TINTA}
          strokeOpacity={0.35}
          strokeWidth={0.8}
        />
        <ellipse
          cx={ANCHO / 2 - 5}
          cy={CARA / 2 - 2}
          rx={6}
          ry={1.6}
          fill={ORO_CLARO}
          opacity={0.7}
        />
        {cara && (
          <path
            d={`M${ANCHO / 2} ${CARA / 2 - 3.6} L${ANCHO / 2 + 1.4} ${CARA / 2} L${ANCHO / 2} ${CARA / 2 + 3.6} L${ANCHO / 2 - 1.4} ${CARA / 2} Z M${ANCHO / 2 - 6} ${CARA / 2} L${ANCHO / 2} ${CARA / 2 - 1} L${ANCHO / 2 + 6} ${CARA / 2} L${ANCHO / 2} ${CARA / 2 + 1} Z`}
            fill={TINTA}
            opacity={0.55}
          />
        )}
      </svg>
    </View>
  );
}
